import { createRootRouteWithContext, Outlet } from "@tanstack/react-router"
import { type Auth0ContextInterface } from "@auth0/auth0-react"
import NotFound from "~/components/reusable/NotFound"
import { type api } from "~/utils/api"
import { useTheme } from "next-themes"
import { ToastContainer } from "react-toastify"
import { CheckCircle, Info, Warning, XCircle } from "@phosphor-icons/react"
import { useNavigationHotkeys } from "~/hooks/useNavigationHotkeys"

const ThemedToastContainer = () => {
  const { resolvedTheme } = useTheme()

  return (
    <ToastContainer
      position="bottom-right"
      autoClose={5000}
      newestOnTop={false}
      closeOnClick
      pauseOnHover
      theme={resolvedTheme === "dark" ? "dark" : "light"}
      className="label-base"
      icon={({ type }) => {
        switch (type) {
          case "info":
            return <Info className="text-fg-neutral-icon" size={16} weight="fill" />
          case "error":
            return <XCircle className="text-fg-error-icon" size={16} weight="fill" />
          case "success":
            return <CheckCircle className="text-fg-success-icon" size={16} weight="fill" />
          case "warning":
            return <Warning className="text-fg-warning-icon" size={16} weight="fill" />
          default:
            return null
        }
      }}
    />
  )
}

interface MyRouterContext {
  auth: Auth0ContextInterface
  /** tRPC query helpers on the app's query cache, so routes can load data before rendering. */
  trpcUtils: ReturnType<typeof api.useUtils>
}

const RootComponent = () => {
  useNavigationHotkeys()

  return (
    <>
      <Outlet />
      <ThemedToastContainer />
    </>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
})
